/**
 * Pure-function filter + sort for the Blocked-users settings list.
 *
 * Threat model:
 *
 * All filtering is in-memory over the `BlockedUserRow[]` we already built
 * locally from `BlockListStore.allBlocked()`. There is no function here
 * that takes a search string and a network connection — the type signatures
 * themselves preclude leaking the query to the server. This matters because
 * if "user A is filtering for user B in their block list" ever became a
 * server-side query, the server could log:
 *
 *   - the existence of user A's block list (already known),
 *   - WHO user A is currently looking for in that list,
 *   - THE TIMING of user A's interest in that target.
 *
 * All three would defeat the deniable-block guarantee provided by
 * `BlockListStore`. The defence is pure-function design: this module has
 * no `fetch`, no WebSocket, no async.
 *
 * Sort orders are fully local too.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BlockedUsersListPolicy.swift`.
 */

import type { BlockedUserRow } from "./BlockedUsersRowModel";
import type { BlockListEntry } from "../block-list/BlockListEntry";

// ---------------------------------------------------------------------------
// Sort order
// ---------------------------------------------------------------------------

/**
 * Sort order presented in the screen header.
 *
 * - `'recent'` — most recent block first (default; matches `allBlocked()` order).
 * - `'oldest'` — oldest block first.
 * - `'alphabetical'` — case-insensitive locale-aware compare on `displayName`.
 */
export type BlockedUsersSortOrder = "recent" | "oldest" | "alphabetical";

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

/**
 * Substring-match filter on `displayName`. Case-insensitive.
 * Whitespace-only queries return all rows. The empty query returns all rows.
 *
 * Operates over an in-memory `BlockedUserRow[]` only — there is no network
 * egress.
 */
export function filter(
  rows: BlockedUserRow[],
  searchQuery: string,
): BlockedUserRow[] {
  const trimmed = searchQuery.trim();
  if (trimmed === "") return rows;
  const needle = trimmed.toLowerCase();
  // For in-roster rows the user-meaningful name is `displayName`. For
  // not-in-roster rows `displayName` is the rendered stub
  // ("<prefix>… (Unknown user)") whose generic suffix would otherwise
  // accidentally match queries like "o" via "Unknown" — so fall back to
  // the raw `userId` for those rows.
  return rows.filter((r) => {
    const haystack = r.isInRoster ? r.displayName : r.userId;
    return haystack.toLowerCase().includes(needle);
  });
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

/**
 * Sort `rows` according to `order`.
 *
 * `entriesByUserId` is a precomputed map from userId to the authoritative
 * `BlockListEntry` so we don't have to re-walk the store inside the
 * comparator. The caller (the screen view model) builds the map once per
 * refresh.
 *
 * Tiebreaker is the rows' original index — this gives a stable sort across
 * refreshes when two rows share the same `blockedAt` second.
 */
export function sort(
  rows: BlockedUserRow[],
  order: BlockedUsersSortOrder,
  entriesByUserId: Map<string, BlockListEntry>,
): BlockedUserRow[] {
  // Zip with original index for stable sort.
  const indexed: Array<[number, BlockedUserRow]> = rows.map((r, i) => [i, r]);

  const sorted: Array<[number, BlockedUserRow]> = [...indexed].sort(
    (lhs, rhs) => {
      const [li, lr] = lhs;
      const [ri, rr] = rhs;

      switch (order) {
        case "recent": {
          const l = entriesByUserId.get(lr.userId)?.blockedAt ?? 0;
          const r = entriesByUserId.get(rr.userId)?.blockedAt ?? 0;
          if (l !== r) return r - l; // descending
          return li - ri; // stable: preserve original order
        }
        case "oldest": {
          const l = entriesByUserId.get(lr.userId)?.blockedAt ?? 0;
          const r = entriesByUserId.get(rr.userId)?.blockedAt ?? 0;
          if (l !== r) return l - r; // ascending
          return li - ri;
        }
        case "alphabetical": {
          const cmp = lr.displayName.localeCompare(rr.displayName, undefined, {
            sensitivity: "base",
          });
          if (cmp !== 0) return cmp;
          return li - ri;
        }
      }
    },
  );

  return sorted.map(([, row]) => row);
}
